/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import {
  AggregationValueType,
  Alternative,
  TableAggregationType,
  TableColumn,
  TableColumnType,
  UniversalTrackerBlueprintMin,
  UniversalTrackerMin,
  UniversalTrackerPlain,
  VariationDataSource,
  type ValueValidation,
} from '../types/universalTracker';
import {
  AggregationMode,
  ColumnValueAggregation,
  UtrValueType,
  ValueAggregation,
  ValueAggregationChildrenCompatibility,
  ValueAggregationSiblingsCompatibility,
  standards,
} from '@g17eco/core';
import { DataPeriods } from '@utils/dataPeriods';
import { DATE, formatDate, subtractDate } from './date';
import { getDecimalNumber, isDefinedNumber } from './number';
import { Option } from '@g17eco/types/valueList';
import { ValueData, ValueDataData } from '@g17eco/types/universalTrackerValue';
import { SURVEY } from '@constants/terminology';
import UniversalTrackerClassInterface from '../model/UniversalTrackerClassInterface';

/** @deprecated replaced by enum UtrValueType */
export const UtrValueTypes = {
  calculation: 'calculation',
  number: 'number',
  percentage: 'percentage',
  sample: 'sample',
  numericValueList: 'numericValueList',
  text: 'text',
  date: 'date',
  valueList: 'valueList',
  valueListMulti: 'valueListMulti',
  textValueList: 'textValueList',
  table: 'table',
}

export const UtrValueTypeMap = {
  [UtrValueType.Text]: 'Text',
  [UtrValueType.Date]: 'Date',
  [UtrValueType.ValueList]: 'List',
  [UtrValueType.ValueListMulti]: 'Multiple-choice List',
  [UtrValueType.NumericValueList]: 'Multiple-choice List (Numerical)',
  [UtrValueType.TextValueList]: 'List',
  [UtrValueType.Table]: 'Table',
  [UtrValueType.Number]: 'Numeric',
  [UtrValueType.Percentage]: 'Percentage',
  [UtrValueType.Sample]: 'Sample or Proportion',
};

export const primitiveNumberTypes = [
  UtrValueTypes.number,
  UtrValueTypes.percentage,
  UtrValueTypes.sample
];


export function isNumeric(utr: Pick<UniversalTrackerMin, 'valueType'>) {
  switch (utr.valueType) {
    case UtrValueTypes.number:
    case UtrValueTypes.percentage:
    case UtrValueTypes.sample:
    case UtrValueTypes.numericValueList:
      return true;
    case UtrValueTypes.text:
    case UtrValueTypes.date:
    case UtrValueTypes.valueList:
    case UtrValueTypes.valueListMulti:
    default:
      return false;
  }
}

export function canAddTarget(utr: Pick<UniversalTrackerPlain, 'valueType' | 'valueValidation'>) {
  switch (utr.valueType) {
    case UtrValueTypes.number:
    case UtrValueTypes.percentage:
    case UtrValueTypes.numericValueList:
      return true;
    case UtrValueTypes.table:
      if (utr.valueValidation?.table?.validation?.maxRows === 1 && hasNumericColumnType(utr)) {
        return true;
      }
      return false;
    case UtrValueTypes.sample:
    case UtrValueTypes.text:
    case UtrValueTypes.date:
    case UtrValueTypes.valueList:
    case UtrValueTypes.valueListMulti:
    default:
      return false;
  }
}

export function getPeriodRange(period: DataPeriods | undefined, date: string) {
  switch (period) {
    case DataPeriods.Monthly:
      return {
        period,
        from: subtractDate(date, 1, 'months'),
        to: formatDate(date, DATE.MONTH_YEAR, true),
      };
    case DataPeriods.Quarterly:
      return {
        period,
        from: subtractDate(date, 3, 'months'),
        to: formatDate(date, DATE.MONTH_YEAR, true),
      };
    case DataPeriods.Yearly:
    default:
      return {
        period: DataPeriods.Yearly,
        from: subtractDate(date, 1, 'years'),
        to: formatDate(date, DATE.MONTH_YEAR, true),
      };
  }
}

export const isCheckboxType = (type: string) => [
  UtrValueTypes.numericValueList,
  UtrValueTypes.textValueList,
].includes(type)

export function getTableConfiguration(utr: UniversalTrackerClassInterface | Pick<UniversalTrackerPlain, 'valueType' | 'valueValidation'>, ignoreType = false) {
  const u = 'getRaw' in utr ? utr.getRaw() : utr;
  const checkTableType = !ignoreType && u.valueType !== 'table'
  if (checkTableType || !u.valueValidation) {
    return undefined;
  }
  return u.valueValidation.table;
}

// Handle conversion between textValueList/numericValueList to valueListMulti
const getValueDataMulti = (data: ValueData['data']) => {
  if (Array.isArray(data)) {
    return data;
  }

  if (typeof data === 'object') {
    return Object.keys(data);
  }
  return [];
};

interface ValueDataArrayProps {
  utrValueType: UtrValueType;
  data: ValueData['data'];
  unit: string;
  numberScale?: string;
  displayCheckbox?: {
    [key: string]: boolean;
  };
  value: number | string | undefined;
  options: Option[];
}

export interface ValueDataArrayReturn {
  // This is raw data, not the label...
  label?: string;
  // Actually resolved label to value list option name
  value: string | number;
  unit: string;
  numberScale?: string;
}

export function getValueDataArray(props: ValueDataArrayProps): ValueDataArrayReturn[] {
  const { utrValueType, data, unit, numberScale, displayCheckbox, value, options: valueListOptions } = props;

  switch (utrValueType) {
    case UtrValueType.ValueList:
      return [{
        // @TODO: This is label not resolved here?
        label: data,
        value: data ? getValueListData(data, valueListOptions) : 'N/A',
        unit,
        numberScale,
      }];
    case UtrValueType.ValueListMulti:
      return getValueDataMulti(data).map((k) => ({
        // @TODO: This is label not resolved here?
        label: k,
        value: getValueListData(k, valueListOptions),
        unit,
        numberScale,
      }));
    case UtrValueType.NumericValueList:
      return valueListOptions.map((item: { code: string }) => {
        const k = item.code;
        return {
          label: getValueListData(k, valueListOptions),
          value: (!displayCheckbox || displayCheckbox[k]) && data && !isNaN(data[k]) ? data[k] : 'N/A',
          unit,
          numberScale,
        }
      });
    case UtrValueType.TextValueList:
      return valueListOptions.map((item: { code: string }) => {
        const k = item.code;
        return {
          label: getValueListData(k, valueListOptions),
          value: (!displayCheckbox || displayCheckbox[k]) && data && data[k] ? data[k] : 'N/A',
          unit,
          numberScale,
        }
      });
    default:
      return [{
        value: convertToNumberOrString(value),
        unit,
        numberScale,
      }];
  }
}

const convertToNumberOrString = (value: undefined | string | number): number | string => {
  if (value === undefined || isNaN(value as any)) {
    return 'N/A';
  }
  return value;
}

export const getQuestionShortPrefix = (utr: UniversalTrackerClassInterface) => {
  const alternativeCode = utr.getType();
  return utr.getShortPrefix(alternativeCode);
}

export const getQuestionName = (utr: UniversalTrackerClassInterface) => {
  const alternativeCode = utr.getType();
  return utr.getName(alternativeCode);
}

export const getQuestionFullTitle = (utr: UniversalTrackerClassInterface, code: string) => {
  return `${utr.getShortPrefix(code)} ${utr.getName(code)}`;
};

function __hasAlternativeInfo(utr: UniversalTrackerPlain, alternativeCode = '') {
  if (!utr.alternatives) {
    return false;
  }
  return alternativeCode in utr.alternatives;
}

function __get(attribute: keyof UniversalTrackerPlain | keyof Alternative, utr: UniversalTrackerPlain, alternativeCodes?: string[]): unknown {
  let returnAttribute = utr[attribute as keyof UniversalTrackerPlain];

  if (!alternativeCodes) {
    return returnAttribute;
  }

  alternativeCodes.forEach(code => {
    if (code === utr.type) {
      return returnAttribute = utr[attribute as keyof UniversalTrackerPlain];
    }
    if (__hasAlternativeInfo(utr, code)) {
      return returnAttribute = utr.alternatives?.[code]?.[attribute as keyof Alternative];
    }
  });

  return returnAttribute;
}

export function getName(utr: UniversalTrackerPlain, alternativeCodes?: string[]): string { return __get('name', utr, alternativeCodes) as string; }

export const isNumericTableColumnType = (type: TableColumnType) => {
  return [TableColumnType.Number, TableColumnType.Percentage].includes(type);
};

export const isNumericValueType = (valueType: UtrValueType) => {
  return [UtrValueType.Number, UtrValueType.Percentage, UtrValueType.NumericValueList].includes(valueType);
};

export const hasNumericColumnType = ({ valueValidation }: Pick<UniversalTrackerPlain, 'valueValidation'>) => {
  const columns = valueValidation?.table?.columns;
  return columns?.some((column) => isNumericTableColumnType(column.type));
};

export const isSimpleNumericColumnType = (column: Pick<TableColumn, 'type' | 'calculation'>) => {
  return [TableColumnType.Number, TableColumnType.Percentage].includes(column.type) && !column.calculation?.formula;
};

export const isCalculatedColumn = (column: Pick<TableColumn, 'type' | 'calculation'>) => {
  return [TableColumnType.Number, TableColumnType.Percentage].includes(column.type) && column.calculation?.formula;
};

export const hasSimpleNumericColumnType = ({
  valueValidation,
}: Pick<UniversalTrackerPlain, 'valueValidation'>) => {
  const columns = valueValidation?.table?.columns;
  return columns?.some((column) => isSimpleNumericColumnType(column));
};

export const hasNumericInput = (utr: Pick<UniversalTrackerPlain, 'valueType' | 'valueValidation'>) =>
  isNumericValueType(utr.valueType as UtrValueType) || hasSimpleNumericColumnType({ valueValidation: utr.valueValidation });

export const isNumberOrTextValueListType = (utr: UniversalTrackerClassInterface) => {
  return [UtrValueType.NumericValueList, UtrValueType.TextValueList].includes(utr.getValueType());
}

export const isSingleRowTableType = (utr: UniversalTrackerClassInterface | Pick<UniversalTrackerPlain, 'valueType' | 'valueValidation'>) => {
  if ('getValueType' in utr) {
    // UTR must be UniversalTracker
    const tableConfiguration = getTableConfiguration(utr);
    return utr.getValueType() === UtrValueType.Table && tableConfiguration?.validation?.maxRows === 1;
  }
  return utr.valueType === UtrValueType.Table && Number(utr.valueValidation?.table?.validation?.maxRows) === 1;
};

export const isMultiRowTableType = (utr: UniversalTrackerClassInterface | Pick<UniversalTrackerPlain, 'valueType' | 'valueValidation'>) => {
  if ('getValueType' in utr) {
    // UTR must be UniversalTracker
    const tableConfiguration = getTableConfiguration(utr);
    return utr.getValueType() === UtrValueType.Table && tableConfiguration?.validation?.maxRows !== 1;
  }
  return utr.valueType === UtrValueType.Table && Number(utr.valueValidation?.table?.validation?.maxRows) !== 1;
};

export const hasStandard = (utr: Pick<UniversalTrackerBlueprintMin, 'type' | 'alternatives'>, code: string) => {
  return utr.type === code || !!utr.alternatives?.[code];
};

export const hasFramework = (utr: Pick<UniversalTrackerBlueprintMin, 'tags'>, subTag: string) => {
  if (!utr.tags) {
    return false;
  }

  return Object.values(utr.tags).flat().includes(subTag);
};

export const getUtrNumberValue = ({
  value,
  decimal,
  hasValueChanged,
  fallback = '',
}: {
  value: string | number | undefined;
  decimal: number | undefined;
  hasValueChanged: boolean | undefined;
  fallback?: number | string;
}) => {
  if (!isDefinedNumber(value)) {
    return fallback;
  }

  if (!isDefinedNumber(decimal) || hasValueChanged) {
    return value;
  }

  // On first render, the value type must be number as it is taken from API
  // Only then should do automatic decimal addition, not on value change
  return typeof value === 'number' ? getDecimalNumber(value, decimal) : value;
};


const getValueListOptionByCode = (options: Option[], code: string) => {
  const option = options.find((option) => option.code === code);
  if (option === undefined) {
    return code;
  }
  return option.name;
}

export const getValueListData = (data: ValueDataData | undefined, options: Option[]) => {
  if (typeof data === 'string') {
    return getValueListOptionByCode(options, data);
  }
  return '';
}

export const getValueListMultiData = (data: ValueDataData | undefined, options: Option[]) => {
  if (Array.isArray(data)) {
    return data.map((code) => getValueListOptionByCode(options, code));
  }
  return [];
}

export const getUtrDecimal = (
  utr: Pick<UniversalTrackerPlain, 'valueType' | 'valueValidation'> | undefined,
  inputName?: string
) => {
  if (!utr) {
    return;
  }
  const valueType: UtrValueType = utr.valueType as UtrValueType;

  const valueValidation: ValueValidation | undefined = utr.valueValidation;

  if (!valueValidation) {
    return;
  }

  if (isNumericValueType(valueType)) {
    return valueValidation?.decimal;
  }

  if (hasSimpleNumericColumnType({ valueValidation })) {
    return getTableColumnDecimal(valueValidation, inputName);
  }

  return;
};

const getTableColumnDecimal = (valueValidation: ValueValidation, inputName?: string) => {
  const tableColumn = valueValidation.table?.columns.find((col: TableColumn) => col.code === inputName);

  if (!tableColumn || !isSimpleNumericColumnType(tableColumn) || tableColumn.calculation) {
    return;
  }

  return tableColumn?.validation?.decimal;
};

export const getInstructionLabel = (alternativeCode: string = '') => {
  return alternativeCode === 'gri'
    ? 'See complete Reporting Guidance and Reporting Recommendation here'
    : `View ${standards[alternativeCode]?.shortName || ''} Instructions`;
};

export const isTableGroupAggregation = (utr: Pick<UniversalTrackerPlain, 'valueValidation'>) => {
  return utr.valueValidation?.table?.aggregation?.type === TableAggregationType.Group;
}

export const VARIATION_DATA_SOURCE_LABEL_MAP = {
  [VariationDataSource.LastMonth]: `Last month’s ${SURVEY.SINGULAR}`,
  [VariationDataSource.LastYear]: `Last year’s ${SURVEY.SINGULAR}`,
  [VariationDataSource.LatestVerified]: 'Latest verified answer',
};

export const checkIsValueListMetric = (utr: Pick<UniversalTrackerPlain, 'valueType' | 'valueValidation'>) => {
  switch (utr.valueType) {
    case UtrValueType.ValueList:
    case UtrValueType.ValueListMulti:
      return true;
    case UtrValueType.Table:
      return utr.valueValidation?.table?.columns.some(checkIsValueListColumn);
    default:
      return false;
  }
};

export const checkIsValueListColumn = (col: Pick<TableColumn, 'type' | 'listId'>) => {
  return (
    [TableColumnType.ValueList, TableColumnType.ValueListMulti].includes(col.type) ||
    (col.type === TableColumnType.Text && col.listId)
  );
};

const isCompatible = (aggregationFn: string, compatibility: { default: ValueAggregation | ColumnValueAggregation, compatible?: (ValueAggregation | ColumnValueAggregation)[] }) => {
  if (!compatibility) {
    return false;
  }
  if (compatibility.default === aggregationFn) {
    return true;
  }
  if (compatibility.compatible?.includes(aggregationFn as ValueAggregation)) {
    return true;
  }
  return false;
}

export function getAggregatorByUniversalTrackerFn(
  utr: Pick<UniversalTrackerPlain, 'valueType' | 'valueAggregation' | 'aggregationConfig' | 'aggregationValueType'>,
  mode: AggregationMode
): ValueAggregation {
  const isCombinedAggregation = mode === AggregationMode.Combined;
  const valueType = (utr.aggregationValueType ?? utr.valueType) as UtrValueType;
  const compatibility = isCombinedAggregation
    ? ValueAggregationSiblingsCompatibility[valueType]
    : ValueAggregationChildrenCompatibility[valueType];

  // First check if there's a mode-specific override in aggregationConfig
  const modeKey = isCombinedAggregation ? AggregationMode.Combined : AggregationMode.Children;
  const modeSpecificAggregation = utr.aggregationConfig?.modes?.[modeKey]?.valueAggregation;
  if (modeSpecificAggregation && isCompatible(modeSpecificAggregation, compatibility)) {
    return modeSpecificAggregation;
  }

  // Fall back to valueAggregation if no mode-specific config or if it's incompatible
  if (utr.valueAggregation && isCompatible(utr.valueAggregation, compatibility)) {
    return utr.valueAggregation;
  }

  // Finally fall back to system default
  return compatibility.default;
}

export const isWeightedAggregationSupportedValueType = (valueType: UtrValueType | string) =>
  valueType === UtrValueType.Number || valueType === UtrValueType.Percentage;

export const shouldEnforceWeightedAggregationTableUI = (params: {
  valueType: UtrValueType;
  aggregationValueType: AggregationValueType | undefined;
  initiativeAggregationValueType: AggregationValueType | undefined;
  isAutoOptIn: boolean;
}) => {
  const { valueType, aggregationValueType, initiativeAggregationValueType, isAutoOptIn } = params;

  return (
    isWeightedAggregationSupportedValueType(valueType) &&
    aggregationValueType === UtrValueType.Table &&
    (initiativeAggregationValueType === UtrValueType.Table || isAutoOptIn)
  );
};

export const isPercentageValueType = (valueType: UtrValueType | string) => valueType === UtrValueType.Percentage;
